import { cn } from "@/utils/cn";
import { X } from "lucide-react";
import { useDialogAccessibility } from "@/components/ui/dialogUtils";
import {
  useId,
  useRef,
  type HTMLAttributes,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export type DrawerPlacement = "left" | "right";
export type DrawerSize = "sm" | "md" | "lg" | "xl" | "full";

export type DrawerProps = PropsWithChildren<
  Omit<HTMLAttributes<HTMLDivElement>, "title"> & {
    open: boolean;
    onClose: () => void;
    title?: ReactNode;
    description?: ReactNode;
    footer?: ReactNode;
    placement?: DrawerPlacement;
    size?: DrawerSize;
    hasBackdrop?: boolean;
    closeOnBackdropClick?: boolean;
    disableEscape?: boolean;
    showCloseButton?: boolean;
    busy?: boolean;
    testId?: string;
    contentClassName?: string;
  }
>;

const sizeClasses: Record<DrawerSize, string> = {
  sm: "max-w-xs",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  full: "max-w-full",
};

export function Drawer({
  open,
  onClose,
  title,
  description,
  footer,
  placement = "right",
  size = "md",
  hasBackdrop = true,
  closeOnBackdropClick = true,
  disableEscape = false,
  showCloseButton = true,
  busy = false,
  testId = "drawer",
  className,
  contentClassName,
  children,
  ...rest
}: DrawerProps) {
  const titleId = useId();
  const descId = useId();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Focus management, focus trap, escape key, and body scroll lock
  useDialogAccessibility({
    open,
    containerRef: drawerRef,
    onClose,
    disableEscape,
    busy,
  });

  if (!open || typeof document === "undefined") {
    return null;
  }

  const handleBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && closeOnBackdropClick && !busy) {
      onClose();
    }
  };

  const isLeft = placement === "left";

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 z-[80] overflow-hidden",
        hasBackdrop ? "bg-slate-900/60 backdrop-blur-xs transition-opacity duration-200" : "pointer-events-none",
      )}
      role="presentation"
      onMouseDown={hasBackdrop ? handleBackdropMouseDown : undefined}
      data-testid={`${testId}-backdrop`}
    >
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        className={cn(
          "fixed inset-y-0 flex flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out dark:bg-zinc-950",
          isLeft ? "left-0" : "right-0",
          sizeClasses[size],
          "w-full",
          className,
        )}
        data-testid={testId}
        {...rest}
      >
        {/* Header */}
        {title || showCloseButton ? (
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 dark:border-zinc-800/80">
            <div className="min-w-0 flex-1 pr-3">
              {title ? (
                <div
                  id={titleId}
                  className="text-base font-semibold tracking-tight text-slate-900 dark:text-zinc-50"
                  data-testid={`${testId}-title`}
                >
                  {title}
                </div>
              ) : null}
              {description ? (
                <p
                  id={descId}
                  className="mt-0.5 text-xs text-slate-500 dark:text-zinc-400"
                  data-testid={`${testId}-description`}
                >
                  {description}
                </p>
              ) : null}
            </div>

            {showCloseButton ? (
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                aria-label="Close panel"
                className="shrink-0 -mr-1.5 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-ring)] disabled:opacity-50 dark:text-zinc-500 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                data-testid={`${testId}-close`}
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        ) : null}

        {/* Body */}
        <div
          className={cn(
            "overflow-y-auto px-5 py-4 text-sm text-slate-700 dark:text-zinc-300",
            contentClassName,
          )}
        >
          {children}
        </div>

        {/* Footer */}
        {footer ? (
          <div
            className="mt-auto border-t border-slate-100 px-5 py-3.5 dark:border-zinc-800/80"
            data-testid={`${testId}-footer`}
          >
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
